/**
 * Authentication Store
 * Manages user authentication, session state, and permissions
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '@/services/api'
import type { User, Tenant, LoginCredentials, AuthState } from '@/types'
import { apiErrorFromResponse, getApiErrorMessage } from '@/utils/apiErrorMessage'
import { resetFormatPreferences, syncFormatPreferences } from '@/utils/formatPreferences'

function parseJwtPayload(token: string): Record<string, unknown> | null {
  try {
    return JSON.parse(atob(token.split('.')[1]))
  } catch {
    return null
  }
}

/** Deduplicate concurrent initialize() calls during rapid navigation */
let initPromise: Promise<void> | null = null

/**
 * Background retry ladder for a session whose restore failed with a transient
 * (non-auth) error, e.g. an API blip during a deploy rollout. Delays in ms.
 */
const RESTORE_RETRY_DELAYS_MS = [4000, 10000, 20000]

export const useAuthStore = defineStore('auth', () => {
  // ================================
  // State
  // ================================

  const user = ref<User | null>(null)
  const token = ref<string | null>(localStorage.getItem('numera_token'))
  const storedRefreshToken = ref<string | null>(localStorage.getItem('numera_refresh_token'))
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  /** Pending background-restore timers (ladder state lives per store instance). */
  let restoreRetryTimers: ReturnType<typeof setTimeout>[] = []
  /**
   * True when a session restore failed for a TRANSIENT reason (network/5xx) —
   * the token is still valid, only the profile fetch didn't land. The router
   * guard uses this to keep the user inside the app instead of ejecting a
   * perfectly good session to /login.
   */
  const sessionRestoreDegraded = ref(false)

  // ================================
  // Getters
  // ================================

  const isAuthenticated = computed(() => !!token.value && !!user.value && !isTokenExpired.value)

  const currentUser = computed(() => user.value)

  const userRole = computed(() => user.value?.role || null)

  const canAccess = computed(() => (requiredRole?: User['role']) => {
    if (!user.value) return false
    if (!requiredRole) return true

    const roleHierarchy: Record<string, number> = {
      viewer: 0,
      user: 1,
      accountant: 2,
      admin: 3,
      super_admin: 4
    }

    const currentUserLevel = roleHierarchy[user.value.role] ?? -1
    const requiredLevel = roleHierarchy[requiredRole] ?? 99

    return currentUserLevel >= requiredLevel
  })

  const authState = computed<AuthState>(() => ({
    user: user.value,
    token: token.value,
    isAuthenticated: isAuthenticated.value,
    isLoading: isLoading.value,
    error: error.value
  }))

  // ================================
  // Tenant Integration Helpers
  // ================================

  const userTenantId = computed(() => {
    if (!token.value) return null
    try {
      const payload = JSON.parse(atob(token.value.split('.')[1]))
      return payload.tenantId || null
    } catch {
      return null
    }
  })

  const tokenPermissions = computed(() => {
    if (!token.value) return []
    try {
      const payload = JSON.parse(atob(token.value.split('.')[1]))
      return payload.permissions || []
    } catch {
      return []
    }
  })

  const isTokenExpired = computed(() => {
    if (!token.value) return true
    try {
      const payload = JSON.parse(atob(token.value.split('.')[1]))
      return Date.now() >= payload.exp * 1000
    } catch {
      return true
    }
  })

  // ================================
  // Actions
  // ================================

    /**
   * Initialize auth store from localStorage
   */
  const initialize = async () => {
    // Return existing promise if initialization is already in progress
    if (initPromise) return initPromise

    if (token.value && !user.value) {
      // Check if token is expired first
      if (isTokenExpired.value) {
        console.warn('Token expired during initialization, clearing session')
        await logout()
        return
      }

      initPromise = (async () => {
        try {
          // Initialize tenant context first (required for profile fetch)
          await initializeTenantFromToken()

          // Then fetch user profile
          await fetchProfile()

          // Restore landed: back to a fully healthy session.
          sessionRestoreDegraded.value = false
          clearRestoreRetries()
        } catch (error: any) {
          console.warn('Failed to restore user session:', error.message)

          // Check if it's a 401/403 error (authentication issue)
          if (error.response?.status === 401 || error.response?.status === 403) {
            console.warn('Authentication failed, token may be invalid')
            await logout()
          } else if (!userTenantId.value) {
            // The token can't yield a tenant (tampered/legacy/malformed JWT) —
            // the session can never recover, so don't park the user in a
            // degraded in-app state where every request 400s on the missing
            // tenant header. Eject to /login like any other dead session.
            console.warn('Token carries no tenant, clearing session')
            await logout()
          } else {
            // For other errors (network, server), don't logout immediately:
            // mark the session degraded (the router guard keeps the user in
            // the app) and retry the restore in the background.
            console.error('Session restore failed with non-auth error:', error)
            sessionRestoreDegraded.value = true
            scheduleRestoreRetries()
          }
        } finally {
          initPromise = null
        }
      })()

      return initPromise
    }
  }

  function clearRestoreRetries () {
    restoreRetryTimers.forEach(t => clearTimeout(t))
    restoreRetryTimers = []
  }

  /**
   * Marca la sesión degradada DESDE FUERA del ciclo de restore (c610): el
   * guard acota initialize() con withBootDeadline y, cuando el plazo vence
   * mientras la petición sigue en vuelo, este flag hace que el requiereAuth
   * passthrough mantenga al usuario dentro y el BootStatusBanner informe.
   * Es seguro llamarlo en cualquier momento: si el restore en vuelo luego
   * falla de verdad repetirá el marcaje, y si aterriza bien pondrá el flag
   * en false (línea "restore landed").
   */
  function markSessionDegraded () {
    if (!token.value || user.value) return
    sessionRestoreDegraded.value = true
  }

  /**
   * Queue a bounded ladder of background restore retries. No-ops while a
   * ladder is already pending so repeated failures don't stack timers; once
   * the ladder is exhausted, the next failed initialize() starts a new one.
   */
  function scheduleRestoreRetries () {
    if (restoreRetryTimers.length > 0) return
    RESTORE_RETRY_DELAYS_MS.forEach(delay => {
      const timer = setTimeout(() => {
        restoreRetryTimers = restoreRetryTimers.filter(t => t !== timer)
        if (token.value && !user.value) initialize()
      }, delay)
      restoreRetryTimers.push(timer)
    })
  }

  /**
   * Initialize tenant context from JWT token
   */
  const applyTenantBootstrap = async (tenantHint?: Partial<Tenant> | null) => {
    const { useTenantStore } = await import('@/stores/tenant')
    const tenantStore = useTenantStore()

    if (tenantHint?.slug && tenantHint?.id) {
      tenantStore.setCurrentTenant(tenantHint as Tenant)
    } else if (token.value) {
      const payload = parseJwtPayload(token.value)
      const tenantId = payload?.tenantId as string | undefined
      const tenantSlug = payload?.tenantSlug as string | undefined
      if (tenantId && tenantSlug && tenantStore.currentTenant?.id !== tenantId) {
        tenantStore.setCurrentTenant({
          id: tenantId,
          slug: tenantSlug,
          name: (payload?.name as string) || tenantSlug,
          status: 'active',
          businessInfo: {},
          features: tenantHint?.features || {},
          settings: {
            currency: 'DOP',
            timezone: 'America/Santo_Domingo',
            language: 'es',
            fiscalYearStart: '01-01',
          },
          createdAt: '',
          updatedAt: '',
        } as Tenant)
      }
    }

    const tenantId = userTenantId.value
    if (!tenantId) {
      throw new Error('No tenant ID found in token')
    }

    // Only short-circuit on a tenant that carries a REAL feature map. On cold boot
    // (full page reload) this function runs with no hint, so the branch above
    // synthesises a tenant from the JWT — which carries no feature info, leaving
    // `features` as `{}`. Returning that synthetic tenant here would lock it in: the
    // router's tenant gate then sees `hasTenant && slug match` and skips the by-slug
    // fetch, so the lenient hasFeature() treats every gated module (projects,
    // production, landedCosts…) as available and the client 403s on use. Falling
    // through to fetchAvailableTenants() replaces it with the server's derived map.
    const existing = tenantStore.currentTenant
    const hasRealFeatures = existing?.id === tenantId
      && existing?.features
      && typeof existing.features === 'object'
      && Object.keys(existing.features as Record<string, boolean>).length > 0
    if (hasRealFeatures) {
      return existing
    }

    const tenants = await tenantStore.fetchAvailableTenants()
    const currentTenant = tenants.find(t => t.id === tenantId)
    if (currentTenant) {
      tenantStore.setCurrentTenant(currentTenant)
      return currentTenant
    }

    if (tenantStore.currentTenant?.id === tenantId) {
      return tenantStore.currentTenant
    }

    throw new Error(`Tenant ${tenantId} not found in available tenants`)
  }

  const initializeTenantFromToken = async () => {
    try {
      await applyTenantBootstrap()
    } catch (error: any) {
      console.error('Failed to initialize tenant context:', error)
      // Rethrow the ORIGINAL error: wrapping it in a plain Error would strip
      // `error.response`, so initialize()'s 401/403 → logout check could never
      // fire for tenant-bootstrap failures (e.g. an invalid token) and every
      // auth failure looked "transient", stranding the user in-app.
      throw error
    }
  }

  /**
   * Login user with credentials
   */
  const login = async (credentials: LoginCredentials) => {
    try {
      isLoading.value = true
      error.value = null

      const response = await authApi.login(credentials)

      if (response.data.success) {
        const { user: userData, token: authToken, tenant: tenantData, refreshToken: newRefresh } = response.data.data

        user.value = userData
        syncFormatPreferences(userData?.preferences)
        token.value = authToken
        if (newRefresh) {
          storedRefreshToken.value = newRefresh
          localStorage.setItem('numera_refresh_token', newRefresh)
        }

        // Store token in localStorage
        localStorage.setItem('numera_token', authToken)

        // A fresh login supersedes any degraded-restore state.
        sessionRestoreDegraded.value = false
        clearRestoreRetries()

        await applyTenantBootstrap(tenantData ?? null)

        return { success: true, user: userData, token: authToken, tenant: tenantData }
      } else {
        throw apiErrorFromResponse(response, 'Error al iniciar sesión')
      }
    } catch (err: any) {
      // Store the error for the auth store
      error.value = getApiErrorMessage(err, 'Error al iniciar sesión')

      // Create a more detailed error object to throw
      const loginError = err as Error & { response?: { data?: { message?: string }; status?: number }; status?: number }

      throw loginError
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Initialize tenant context after login
   * Fetches available tenants and sets up tenant context
   */
  const initializeTenantContext = async () => {
    try {
      if (!isAuthenticated.value) {
        throw new Error('User must be authenticated to initialize tenant context')
      }

      // Import tenant store dynamically to avoid circular dependency
      const { useTenantStore } = await import('@/stores/tenant')
      const tenantStore = useTenantStore()

      // Fetch available tenants
      const tenants = await tenantStore.fetchAvailableTenants()

      return tenants
    } catch (err: any) {
      console.error('Failed to initialize tenant context:', err)
      throw err
    }
  }

  // Note: Tenant switching functionality removed since users are assigned to a single tenant

  /**
   * Logout user and clear session
   */
  const logout = async (options?: { redirect?: boolean }) => {
    const shouldRedirect = options?.redirect !== false

    try {
      if (token.value) {
        await authApi.logout(storedRefreshToken.value || localStorage.getItem('numera_refresh_token') || undefined)
      }
    } catch (error) {
      console.warn('Logout API call failed:', error)
    } finally {
      user.value = null
      resetFormatPreferences()
      token.value = null
      storedRefreshToken.value = null
      error.value = null
      isLoading.value = false
      sessionRestoreDegraded.value = false
      clearRestoreRetries()

      localStorage.removeItem('numera_token')
      localStorage.removeItem('numera_refresh_token')

      try {
        const { useTenantStore } = await import('@/stores/tenant')
        const tenantStore = useTenantStore()
        tenantStore.clearTenant()
      } catch (error) {
        console.warn('Failed to clear tenant context:', error)
      }

      if (shouldRedirect && window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }
  }

  /**
   * Fetch user profile
   */
  const fetchProfile = async () => {
    try {
      isLoading.value = true
      error.value = null

      const response = await authApi.getProfile()

      if (response.data.success) {
        user.value = response.data.data.user || response.data.data
        syncFormatPreferences(user.value?.preferences)
        return user.value
      } else {
        throw apiErrorFromResponse(response, 'Error al cargar el perfil')
      }
    } catch (err: any) {
      const errorMessage = getApiErrorMessage(err, 'Error al cargar el perfil')
      error.value = errorMessage

      // Log detailed error information for debugging
      if (import.meta.env.DEV) {
        console.error('Profile fetch error details:', {
          status: err.response?.status,
          statusText: err.response?.statusText,
          message: errorMessage,
          url: err.config?.url
        })
      }

      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Refresh authentication token
   */
  /**
   * Clear only the credentials (access + refresh token), keeping user/tenant
   * state. Used when the session is definitively unrecoverable so the 401
   * interceptor's logout branch fires; full teardown lives in logout().
   */
  const clearTokens = () => {
    token.value = null
    storedRefreshToken.value = null
    localStorage.removeItem('numera_token')
    localStorage.removeItem('numera_refresh_token')
  }

  const refreshToken = async (): Promise<string | false> => {
    const stored = storedRefreshToken.value || localStorage.getItem('numera_refresh_token')
    if (!stored) {
      // No refresh token at all: the session can never be refreshed, so clear
      // the stale access token — otherwise the 401 interceptor retries the
      // request with it, 401s again, and never reaches its logout branch.
      clearTokens()
      return false
    }
    try {
      const response = await authApi.refreshToken(stored)

      if (response.data.success) {
        const { token: newToken, refreshToken: newRefresh } = response.data.data
        token.value = newToken
        localStorage.setItem('numera_token', newToken)
        if (newRefresh) {
          storedRefreshToken.value = newRefresh
          localStorage.setItem('numera_refresh_token', newRefresh)
        }
        return newToken
      } else {
        throw new Error('Token refresh failed')
      }
    } catch (err: any) {
      console.warn('Token refresh failed:', err)
      // Definitive rejection by the server (invalid/expired/revoked refresh
      // token): clear the tokens so the 401 interceptor's logout branch fires
      // instead of retrying forever with the dead access token. Transient
      // failures (network drop, 5xx) keep the tokens — the degraded-session
      // design lets the user keep working until the next retry.
      const status = err?.response?.status
      if (status === 400 || status === 401 || status === 403) {
        clearTokens()
      }
      return false
    }
  }

  /**
   * Update user profile
   */
  const updateProfile = async (profileData: { name?: string; email?: string; phone?: string }) => {
    try {
      isLoading.value = true
      error.value = null

      const response = await authApi.updateProfile(profileData)

      if (response.data.success) {
        user.value = response.data.data.user
        syncFormatPreferences(user.value?.preferences)
        return response.data.data.user
      } else {
        throw apiErrorFromResponse(response, 'Error al actualizar el perfil')
      }
    } catch (err: any) {
      error.value = getApiErrorMessage(err, 'Error al actualizar el perfil')
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Check if user has specific permission.
   * Uses role hierarchy: super_admin and admin have all permissions,
   * lower roles derive permissions from the hierarchy via canAccess.
   */
  const hasPermission = (permission: string): boolean => {
    if (!user.value) return false

    // super_admin and admin have all permissions
    if (canAccess.value('admin')) return true

    const [resource, action] = permission.split('.')
    if (!action) return false

    // Read-only permissions are available to all authenticated users (viewer+)
    if (action === 'read') return true

    // Write permissions require at least 'user' role
    if (['create', 'update', 'delete'].includes(action)) {
      return canAccess.value('user')
    }

    return false
  }

  /**
   * Clear error state
   */
  const clearError = () => {
    error.value = null
  }

  // ================================
  // Profile Management Actions
  // ================================

  /**
   * Update user preferences
   */
  const updatePreferences = async (preferences: any) => {
    try {
      isLoading.value = true
      error.value = null

      const response = await authApi.updatePreferences(preferences)

      if (response.data.success) {
        // Update user preferences in current user object
        if (user.value) {
          user.value.preferences = { ...user.value.preferences, ...response.data.data.preferences }
          syncFormatPreferences(user.value.preferences)
        }
        return response.data.data.preferences
      } else {
        throw apiErrorFromResponse(response, 'Error al actualizar las preferencias')
      }
    } catch (err: any) {
      error.value = getApiErrorMessage(err, 'Error al actualizar las preferencias')
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Change password
   */
  const changePassword = async (passwordData: { currentPassword: string; newPassword: string }) => {
    try {
      isLoading.value = true
      error.value = null

      const response = await authApi.changePassword(passwordData)

      if (response.data.success) {
        return true
      } else {
        throw apiErrorFromResponse(response, 'Error al cambiar la contraseña')
      }
    } catch (err: any) {
      error.value = getApiErrorMessage(err, 'Error al cambiar la contraseña')
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Get active sessions
   */
  const getSessions = async () => {
    try {
      const response = await authApi.getSessions()
      return response.data.data
    } catch (err: any) {
      error.value = getApiErrorMessage(err, 'Error al cargar las sesiones')
      throw err
    }
  }

  /**
   * Revoke session
   */
  const revokeSession = async (sessionId: string) => {
    try {
      const response = await authApi.revokeSession(sessionId)
      return response.data.success
    } catch (err: any) {
      error.value = getApiErrorMessage(err, 'Error al revocar la sesión')
      throw err
    }
  }

    const uploadAvatar = async (file: File) => {
    try {
      isLoading.value = true
      error.value = null

      const response = await authApi.uploadAvatar(file)

      // Update user with new avatar URL
      if (user.value) {
        (user.value as any).avatarUrl = response.data.avatarUrl
      }

      // Force refresh of profile to ensure UI updates
      await fetchProfile()

      return response
    } catch (err: any) {
      const errorMessage = getApiErrorMessage(err, 'Error al subir el avatar')
      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      isLoading.value = false
    }
  }

    const deleteAvatar = async () => {
    try {
      isLoading.value = true
      error.value = null

      const response = await authApi.deleteAvatar()

      // Remove avatar URL from user
      if (user.value) {
        (user.value as any).avatarUrl = null
      }

      return response
    } catch (err: any) {
      const errorMessage = getApiErrorMessage(err, 'Error al eliminar el avatar')
      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      isLoading.value = false
    }
  }

  // ================================
  // Return store interface
  // ================================

  return {
    // State
    user: computed(() => user.value),
    token: computed(() => token.value),
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),

    // Getters
    isAuthenticated,
    currentUser,
    userRole,
    canAccess,
    authState,
    sessionRestoreDegraded: computed(() => sessionRestoreDegraded.value),

    // Tenant Integration
    userTenantId,
    tokenPermissions,
    isTokenExpired,

    // Actions
    markSessionDegraded,
    initialize,
    login,
    logout,
    fetchProfile,
    refreshToken,
    updateProfile,
    updatePreferences,
    changePassword,
    getSessions,
    revokeSession,
    uploadAvatar,
    deleteAvatar,
    hasPermission,
    clearError,

    // Tenant Actions
    initializeTenantContext
    // Note: switchToTenant removed since users can't switch tenants
  }
}, {
  persist: true
})
